import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import IconButton from 'components/common/IconButton';
import FailedRequest from 'components/requests-response/FailedRequest';
import SuccessRequest from 'components/requests-response/SuccessRequest';
import { PrioritySelect } from 'hooks/useTicketsTable';
import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Col,
  Form,
  Row,
} from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import MaskedInput from 'react-text-mask';
import BusinessScheduleService from 'services/BusinessSchedule.service';
import SlaPoliciesService from 'services/SlaPolicies.service';

const SlaPolicyEdit = () => {
  const { id } = useParams();
  const form = useRef(null);
  const navigate = useNavigate();

  const modelBody = {
    name: '',
    description: '',
    businessSchedule: null,
    slaTargets: [
      { priority: 'PLANNED',  responseTime: '01:00', resolutionTime: '01:00' },
      { priority: 'LOW',      responseTime: '01:00', resolutionTime: '01:00' },
      { priority: 'MEDIUM',   responseTime: '01:00', resolutionTime: '01:00' },
      { priority: 'HIGH',     responseTime: '01:00', resolutionTime: '01:00' },
      { priority: 'CRITICAL', responseTime: '01:00', resolutionTime: '01:00' },
    ],
  };

  const [validated, setValidated] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState({});
  const [body, setBody] = useState(modelBody);
  const [schedules, setSchedules] = useState([]);
  const [scheduleError, setScheduleError] = useState(false);

  useEffect(() => {
    loadSchedules();
  }, []);

  useEffect(() => {
    if (schedules.length > 0) getData();
  }, [schedules]);

  const loadSchedules = async () => {
    const response = await BusinessScheduleService.list();
    if (Array.isArray(response)) setSchedules(response);
  };

  const getData = async () => {
    setBody(modelBody);
    const response = await SlaPoliciesService.slaPoliciesFindById(id);
    if (!response.status) {
      setBody(prevBody => ({
        ...prevBody,
        name: response.name,
        description: response.description,
        businessSchedule: response.businessSchedule || null,
        slaTargets: response.slaTargets,
      }));
    }
  };

  const handleSetBody = (fieldName, fieldValue, slaTargetIndex) => {
    setBody(prevBody => {
      if (slaTargetIndex === undefined) {
        return { ...prevBody, [fieldName]: fieldValue };
      }
      const updatedSlaTargets = [...prevBody.slaTargets];
      updatedSlaTargets[slaTargetIndex] = {
        ...updatedSlaTargets[slaTargetIndex],
        [fieldName]: fieldValue,
      };
      return { ...prevBody, slaTargets: updatedSlaTargets };
    });
  };

  const handleSubmit = async e => {
    if (!body.businessSchedule?.id) {
      setScheduleError(true);
      e.preventDefault();
      return;
    }
    setScheduleError(false);

    if (form.current.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();
      setValidated(true);
      return;
    }

    const payload = {
      name: body.name,
      description: body.description,
      businessSchedule: { id: body.businessSchedule.id },
      slaTargets: body.slaTargets.map(target => ({
        id: target.id,
        priority: target.priority?.priority ?? target.priority,
        responseTime: target.responseTime,
        resolutionTime: target.resolutionTime,
      })),
    };

    const response = await SlaPoliciesService.slaPoliciesEdit(id, payload);

    if (response.status) {
      setError(response);
      setShowError(true);
    } else {
      setSuccess('Política editada com sucesso');
      setShowSuccess(true);
      getData();
    }
  };

  return (
    <>
      <SuccessRequest
        showAlert={showSuccess}
        setShowAlert={() => setShowSuccess(false)}
        message={success}
      />
      <FailedRequest
        message={error.userMessage}
        showAlert={showError}
        setShowAlert={() => setShowError(false)}
      />
      <Row>
        <Col md={9}>
          <FalconComponentCard>
            <Flex direction="row" alignItems="center">
              <IconButton
                onClick={() => navigate(-1)}
                variant="falcon-default"
                size="sm"
                icon="arrow-left"
                className="ms-3"
              />
              <FalconComponentCard.Header title="Editar política de SLA" />
            </Flex>

            <FalconComponentCard.Body>
              <Form validated={validated} ref={form}>
                <Form.Group className="w-75 mb-3">
                  <Form.Label>
                    Nome <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Nome"
                    required
                    value={body.name}
                    onChange={e => handleSetBody('name', e.target.value)}
                  />
                  <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">Insira um nome</Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="w-75 mb-3">
                  <Form.Label>Descrição</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="description"
                    value={body.description}
                    onChange={e => handleSetBody('description', e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="w-75 mb-3">
                  <Form.Label>
                    Horário Comercial <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={body.businessSchedule?.id || ''}
                    isInvalid={scheduleError}
                    onChange={e => {
                      const selected = schedules.find(s => s.id === Number(e.target.value));
                      handleSetBody('businessSchedule', selected || null);
                      if (selected) setScheduleError(false);
                    }}
                  >
                    <option value="">Selecione um horário comercial</option>
                    {schedules.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Form.Select>
                  {scheduleError && (
                    <div className="invalid-feedback d-block">
                      Selecione um horário comercial
                    </div>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Definir a meta do SLA</Form.Label>
                  <div className="table-responsive scrollbar">
                    <table className="table table-bordered table-striped fs-10 mb-0">
                      <thead className="bg-200 text-900">
                        <tr>
                          <th>Prioridade</th>
                          <th>Tempo de primeira resposta</th>
                          <th>Tempo de resolução</th>
                        </tr>
                      </thead>
                      <tbody>
                        {body.slaTargets.map((item, i) => (
                          <tr key={i}>
                            <td>
                              <PrioritySelect
                                title={item.priority?.description}
                                color={item.priority?.color}
                                data={item.priority?.data}
                              />
                            </td>
                            <td>
                              <MaskedInput
                                mask={[/[0-9]/, /\d/, ':', /\d/, /\d/]}
                                placeholder="HH:MM"
                                required
                                guide={false}
                                value={item.responseTime}
                                onChange={e =>
                                  handleSetBody('responseTime', e.target.value, i)
                                }
                                render={(ref, props) => (
                                  <Form.Control ref={ref} {...props} />
                                )}
                              />
                            </td>
                            <td>
                              <MaskedInput
                                mask={[/[0-9]/, /\d/, ':', /\d/, /\d/]}
                                placeholder="HH:MM"
                                required
                                guide={false}
                                value={item.resolutionTime}
                                onChange={e =>
                                  handleSetBody('resolutionTime', e.target.value, i)
                                }
                                render={(ref, props) => (
                                  <Form.Control ref={ref} {...props} />
                                )}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Form.Group>

                <Form.Group className="d-flex justify-content-end">
                  <Link to="/settings/sla-policies" className="btn btn-default me-2">
                    Cancelar
                  </Link>
                  <Button variant="primary" onClick={handleSubmit}>
                    Salvar
                  </Button>
                </Form.Group>
              </Form>
            </FalconComponentCard.Body>
          </FalconComponentCard>
        </Col>

        <Col>
          <FalconComponentCard>
            <FalconComponentCard.Body>
              <p className="fw-bold">Política de SLA</p>
              <p className="fs-10">
                Uma política de Acordo de Nível de Serviço (SLA) permite definir padrões de
                desempenho para sua equipe de suporte. Você pode definir políticas de SLA para
                o tempo em que os agentes devem responder e resolver tickets com base em
                prioridades.
              </p>
              <br />
              <p className="fw-bold">Horário Comercial</p>
              <p className="fs-10">
                Selecione o horário comercial que será usado para calcular os prazos de SLA.
                Todos os targets desta política respeitarão o mesmo horário.
              </p>
            </FalconComponentCard.Body>
          </FalconComponentCard>
        </Col>
      </Row>
    </>
  );
};

export default SlaPolicyEdit;
